/**
 * CalendarWeek — faithful port of the canonical CalendarWeek screen.
 * 5-day work-week grid with time slots, event blocks, resource filter,
 * Day/Week/Month view switcher.
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { SegmentedControl, alpha, Btn, Card, Checkbox, Icon, IconBtn, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useEvents } from "../events/useEvents";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { loadCalendarRange, blockSpanForDay, isScopeForbidden, type CalRangeBlock } from "./cal-range";
import { calendarBlockTime } from "./calendar-block-time";
import { useT, t as i18nT } from "../../lib/i18n";
import { LoadingRegion, SkeletonBox } from "../../lib/loading-skeletons";
import { getDateTimeDefaults } from "@v0y4ge/i18n-runtime";
import { TimeZoneNote, zoneLabel } from "../../shell/TimeZoneNote";
import { civilKey, civilNoon, currentCalendarDate, friendlyCalendarHour, friendlyCalendarWeekRange, friendlyCalendarWeekday, toParts, useDateTimeVersion } from "../../lib/datetime";

/* ── Calendar helpers ─────────────────────────────────────────────── */
const CAL_MS = 24 * 60 * 60 * 1000;

function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function weekStart(d: Date): Date { const x = new Date(d.getFullYear(),d.getMonth(),d.getDate()); x.setDate(x.getDate()-(((x.getDay()+6)%7))); return x; }
function isoWeek(d: Date): number { const t=new Date(Date.UTC(d.getFullYear(),d.getMonth(),d.getDate())); t.setUTCDate(t.getUTCDate()+4-(t.getUTCDay()||7)); const ys=new Date(Date.UTC(t.getUTCFullYear(),0,1)); return Math.ceil(((t.getTime()-ys.getTime())/CAL_MS+1)/7); }

/* ── Fictional calendar event seed ────────────────────────────────── */
/** One event as read, before it is placed: its instants stay raw so the grid can place them in the display zone. */
interface CalRow { id:string; title:string; color:string; type:string; loc?:string; startsAt?:string|null; endsAt?:string|null; }
/** `start`/`end` are the real hours (the label); `gStart`/`gEnd` are the same clamped to the grid's rows. */
interface CalEvent { id:string; title:string; dayOffset:number; start:number; end:number; gStart:number; gEnd:number; color:string; type:string; loc?:string; }

const HOURS = [8,9,10,11,12,13,14,15,16,17,18,19,20]; // ui-config
const ROW_H = 48;
const TIME_W = 60;

/** Lanes for overlapping items: clusters of transitively overlapping items, each item
 *  in the first free column of its cluster; `cols` is the cluster's width.
 *  Mirrors CalendarDay's laneLayout (CC, 2026-10-05). */
function laneLayout(items: { key: string; start: number; end: number }[]): Map<string, { col: number; cols: number }> {
  const out = new Map<string, { col: number; cols: number }>();
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  let cluster: { key: string; col: number }[] = []; let colEnds: number[] = []; let clusterEnd = -Infinity;
  const flush = () => { const cols = Math.max(1, colEnds.length); cluster.forEach((c) => out.set(c.key, { col: c.col, cols })); cluster = []; colEnds = []; };
  for (const it of sorted) {
    if (it.start >= clusterEnd) { flush(); clusterEnd = -Infinity; }
    let col = colEnds.findIndex((end) => end <= it.start);
    if (col === -1) { col = colEnds.length; colEnds.push(it.end); } else colEnds[col] = it.end;
    cluster.push({ key: it.key, col });
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  flush();
  return out;
}

export default function CalendarWeek() {
  const t = useT();
  const phone = useIsMobile(640); // the toolbar stays one height on desktop; a phone keeps the 34px segment tap targets
  const nav = useNav();
  // (CC, 2026-10-05) Same read the Events list uses; a block opens the event drawer when its id is a known event.
  const { events: uiEvents } = useEvents();
  const [weekOffset, setWeekOffset] = useState(0);
  const [calEvents, setCalEvents] = useState<CalRow[] | null>(null);
  // B2 (2026-07-22 docket): anchor "today"/this-week on the tenant display tz,
  // not the browser's local zone (was `new Date()` at module scope) — otherwise
  // the week grid can highlight the wrong day near a tz/day boundary.
  const today = currentCalendarDate();
  const BASE_MON = weekStart(new Date(today.y, today.mo, today.d));
  // The filter's universe of types is derived straight from the events
  // actually on screen — never a hardcoded/static allowlist. A static list
  // can silently drift from the real category labels the API returns (that
  // was the bug: an event's category could never appear in the filter, so it
  // stayed permanently hidden, and "Clear" — which only reset to the same
  // stale allowlist — could never surface it again).
  const CAL_TYPES = useMemo(() => {
    const seen = new Map<string, string>(); // category label -> swatch colour (first event wins)
    for (const e of calEvents ?? []) if (!seen.has(e.type)) seen.set(e.type, e.color);
    return Array.from(seen, ([id, color]) => ({ id, label: id, color }));
  }, [calEvents]);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(() => new Set(CAL_TYPES.map((t)=>t.id)));
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const h = (e: MouseEvent) => { if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false); };
    document.addEventListener("mousedown",h);
    return () => document.removeEventListener("mousedown",h);
  },[filterOpen]);

  // Whenever a category appears that isn't in activeTypes yet (events load,
  // or a later fetch surfaces a label not seen before), default it to
  // visible — that gap is exactly what made "Clear" a no-op.
  useEffect(() => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const t of CAL_TYPES) if (!next.has(t.id)) { next.add(t.id); changed = true; }
      return changed ? next : prev;
    });
  }, [CAL_TYPES]);

  const ws = addDays(BASE_MON, weekOffset*7);
  // UIR-24 (Voyage #249): availability blocks from the typed calendar op — a
  // week that omits OOO/closures presents closed time as bookable.
  const [calBlocks, setCalBlocks] = useState<CalRangeBlock[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    // The five tenant civil dates, as plain y-m-d (CC 0075), not host instants.
    let live = true;
    loadCalendarRange(civilKey(ws), civilKey(addDays(ws, 4)))
      .then(({ events, blocks }) => {
        if (!live) return;
        setLoadError(null);
        setCalEvents(events.map((e): CalRow => ({ id: e.id, title: e.title, color: e.color, type: e.type, loc: e.loc, startsAt: e.startsAt ?? null, endsAt: e.endsAt ?? null })));
        setCalBlocks(blocks);
      })
      .catch((err) => {
        if (!live) return;
        console.warn("getCalendar failed:", err);
        setCalEvents([]); setCalBlocks([]);
        setLoadError(isScopeForbidden(err)
          ? t("calendar.week.scopeForbidden", "Your role's scope doesn't cover this calendar range (SCOPE_FORBIDDEN) — nothing is shown.")
          : t("calendar.week.couldnTLoadTheCalendar", "Couldn't load the calendar. Retry in a moment."));
      });
    return () => { live = false; };
  }, [ws.getTime(), nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const dayDates = [0,1,2,3,4].map((i)=>addDays(ws,i)); // ui-config
  const [d0,d4] = [dayDates[0],dayDates[4]];
  // Columns are civil dates (host-local midnights of the tenant date); the
  // tenant-zone formatters get a noon inside that tenant day (CC 0075).
  const rangeLabel = friendlyCalendarWeekRange(civilNoon(d0), civilNoon(d4));
  /* The grid places events in the tenant display time zone (toParts), so it says which one, the way the other
     time surfaces do (CC, 2026-10-05): a venue-local time on a list then no longer reads as a contradiction. */
  const displayZone = (() => { try { return getDateTimeDefaults().timeZone; } catch { return undefined; } })();
  /* Blocks are placed at render, in the zone the note names (CC, 2026-10-05). They were placed once when the read
     resolved; if the tenant zone was not configured yet that used the browser's zone and froze it, so a viewer
     outside the tenant zone saw every event shifted. useDateTimeVersion re-renders when the zone is configured. */
  const dtVersion = useDateTimeVersion();
  const placed = useMemo(() => (calEvents ?? []).map((e): CalEvent => {
    const sd = e.startsAt ? new Date(e.startsAt) : null;
    const ed = e.endsAt ? new Date(e.endsAt) : sd;
    const sp = sd ? toParts(sd, displayZone) : null;
    const ep = ed ? toParts(ed, displayZone) : null;
    // Day column from the TENANT calendar date (toParts' y/mo/d), never a raw ms diff against `ws`.
    const dayOff = sp
      ? Math.round((Date.UTC(sp.y, sp.mo, sp.d) - Date.UTC(ws.getFullYear(), ws.getMonth(), ws.getDate())) / 86400000)
      : 0;
    const start = sp ? sp.h + sp.mi/60 : 9;
    const end = ep ? ep.h + ep.mi/60 : 10;
    // Clamped to the grid's rows so nothing paints above the first hour into the all-day band; the label keeps the real times.
    const lo = HOURS[0], hi = HOURS[HOURS.length-1] + 1;
    const gStart = Math.min(Math.max(start, lo), hi - 0.5);
    const gEnd = Math.max(Math.min(Math.max(end, start), hi), gStart + 0.5);
    return { id: e.id, title: e.title, dayOffset: dayOff, start, end, gStart, gEnd, color: e.color, type: e.type, loc: e.loc };
  }).filter((e) => e.dayOffset >= 0 && e.dayOffset < 5), [calEvents, dtVersion, displayZone, ws.getTime()]);

  const visible = placed.filter((e)=> activeTypes.has(e.type));
  const hiddenCount = placed.length - visible.length;


  return (
    <Screen rail="calendar" tabs={eventsSectionTabs(nav,"calendar",useEventSectionCounts())} activeTab="events">
      {/* Toolbar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 24px", background:T.white, borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Btn variant="secondary" size="md" onClick={() => nav.go("events")} icon={<Icon.chev dir="left" size={12}/>}>{t("calendar.week.events", "Events")}</Btn>
          <Btn variant="secondary" size="md" onClick={() => setWeekOffset(0)}>{t("calendar.week.today", "Today")}</Btn>
          <IconBtn label={t("calendar.week.previousWeek", "Previous week")} onClick={() => setWeekOffset(weekOffset-1)}><Icon.chev dir="left"/></IconBtn>
          <IconBtn label={t("calendar.week.nextWeek", "Next week")}     onClick={() => setWeekOffset(weekOffset+1)}><Icon.chev dir="right"/></IconBtn>
          <span style={{ fontSize:16, fontWeight:600, color:T.n1 }}>
            {rangeLabel} {t("calendar.week.week10e916fc", "· Week")}{" "}{isoWeek(ws)}
            {weekOffset!==0 ? <span onClick={() => setWeekOffset(0)} style={{ marginLeft:8, fontSize:12, fontWeight:500, color:T.p1, cursor:"pointer" }}>{t("calendar.week.jumpToCurrent", "Jump to current")}</span> : null}
          </span>
          {hiddenCount > 0 ? <Pill tone="warn"><Icon.filter size={10}/> {hiddenCount} {t("calendar.week.hidden", "hidden ·")}<span style={{ textDecoration:"underline", cursor:"pointer" }} onClick={() => setActiveTypes(new Set(CAL_TYPES.map((t)=>t.id)))}>{t("common.actions.clear", "Clear")}</span></Pill> : null}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Type filter */}
          <div ref={filterRef} style={{ position:"relative" }}>
            <Btn variant="secondary" size="md" icon={<Icon.filter size={14}/>} onClick={() => setFilterOpen((o)=>!o)}>
              {t("calendar.week.types", "Types ·")}{calEvents === null ? null : <>{CAL_TYPES.filter((t) => activeTypes.has(t.id)).length}/{CAL_TYPES.length}</>}{/* (CC, 2026-10-05) no 0/0 while the read is pending */}
            </Btn>
            {filterOpen ? (
              <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:50, background:T.white, border:`1px solid ${T.border}`, borderRadius:8, boxShadow:T.shadowLg, padding:6, minWidth:200 }}>
                <div style={{ padding:"6px 10px 8px", fontSize:11, color:T.soft, fontWeight:600, textTransform:"uppercase", letterSpacing:0.04 }}>{t("calendar.week.eventTypes", "Event types")}</div>
                {CAL_TYPES.length === 0 ? <div style={{ padding:"8px 10px", fontSize:12.5, color:T.soft }}>{t("calendar.week.noEventsThisWeekYet", "No events this week yet.")}</div> : null}
                {CAL_TYPES.map((t) => (
                  <label key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:4, cursor:"pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background=T.n6)}
                    onMouseLeave={(e) => (e.currentTarget.style.background="transparent")}>
                    <Checkbox checked={activeTypes.has(t.id)} onChange={() => { const s=new Set(activeTypes); s.has(t.id)?s.delete(t.id):s.add(t.id); setActiveTypes(s); }}/>
                    <span style={{ width:10, height:10, borderRadius:3, background:t.color, flexShrink:0 }}/>
                    <span style={{ fontSize:13, color:T.n1 }}>{t.label}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          <IconBtn label={t("common.actions.refresh", "Refresh")} onClick={() => { setCalEvents(null); setNonce((n) => n + 1); }}><Icon.refresh/></IconBtn>
          <IconBtn label={t("calendar.week.settings", "Settings")} onClick={() => nav.go("settings.calendar")}><Icon.settings/></IconBtn>
          {/* View switcher */}
          <SegmentedControl<"day" | "week" | "month">
            size={phone ? "md" : "sm"}
            ariaLabel={t("calendar.week.ariaLabel", "Calendar view")}
            value="week"
            onChange={(v) => { if (v === "day") nav.go("calendar.day", { day: d0.getDate() });  if (v === "month") nav.go("calendar.month"); }}
            /* The three calendar views are configuration, not data. */
            options={[
              { value: "day", label: t("calendar.week.day", "Day") },
              { value: "week", label: t("calendar.week.week", "Week") },
              { value: "month", label: t("calendar.week.month", "Month") },
            ]}
          />
        </div>
      </div>

      {displayZone ? <div style={{ padding:"6px 24px", background:T.white, borderBottom:`1px solid ${T.border}` }}><TimeZoneNote testid="calendar-week-zone">{t("staffAvailability.tzNeutral", "Times are shown in {zone}.", { zone: zoneLabel(displayZone) })}</TimeZoneNote></div> : null}

      {loadError ? <div style={{ padding:"8px 24px", background:alpha(T.red1,"12"), borderBottom:`1px solid ${alpha(T.red1,"33")}`, color:T.red1, fontSize:12.5, fontWeight:600, flexShrink:0 }}>{loadError}</div> : null}

      {/* Calendar grid */}
      <div style={{ flex:1, overflow:"auto", background:T.white }}>
        {/* Day headers */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px repeat(5, 1fr)`, borderBottom:`1px solid ${T.border}`, position:"sticky", top:0, zIndex:10, background:T.white }}>
          <div/>
          {dayDates.map((d,i) => {
            const isToday = d.getFullYear()===today.y && d.getMonth()===today.mo && d.getDate()===today.d;
            return (
              <div key={i} onClick={() => nav.go("calendar.day",{ day:d.getDate(), month:d.getMonth(), year:d.getFullYear() })} style={{ padding:"10px 8px", textAlign:"center", borderLeft:`1px solid ${T.border}`, cursor:"pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background=T.n6)}
                onMouseLeave={(e) => (e.currentTarget.style.background="transparent")}>
                <div style={{ fontSize:11, color:T.soft, fontWeight:600, textTransform:"uppercase" }}>{friendlyCalendarWeekday(civilNoon(d))}</div>
                <div style={{ marginTop:2, fontSize:16, fontWeight:600, color:isToday?T.white:T.n1, background:isToday?T.p1:"transparent", borderRadius:999, width:28, height:28, display:"inline-flex", alignItems:"center", justifyContent:"center" }}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* All-day strip */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px repeat(5, 1fr)`, borderBottom:`1px solid ${T.border}`, background:T.n6 }}>
          <div style={{ padding:"6px 8px", textAlign:"right", fontSize:10, color:T.soft, textTransform:"uppercase", letterSpacing:0.04 }}>{t("calendar.week.allDay", "All day")}</div>
          {[0,1,2,3,4].map((i) => <div key={i} style={{ minHeight:28, borderLeft:`1px solid ${T.border}`, padding:4 }}/>)}
        </div>

        {/* Time grid */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px repeat(5, 1fr)`, position:"relative" }}>
          {/* Hour labels */}
          <div style={{ borderRight:`1px solid ${T.border}` }}>
            {HOURS.map((h) => (
              <div key={h} style={{ height:ROW_H, paddingTop:4, paddingRight:8, textAlign:"right", fontSize:11, color:T.soft, fontVariantNumeric:"tabular-nums" }}>
                {friendlyCalendarHour(h)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {[0,1,2,3,4].map((dayIdx) => {
            const dayEvs = visible.filter((e) => e.dayOffset === dayIdx);
            const daySpans = calBlocks
              .map((b) => ({ b, span: blockSpanForDay(b, civilKey(dayDates[dayIdx]), HOURS[0], HOURS[HOURS.length-1]+1) }))
              .filter((x): x is { b: CalRangeBlock; span: { startH: number; endH: number } } => x.span !== null);
            const lanes = laneLayout(dayEvs.map((e) => ({ key: e.id, start: e.gStart, end: e.gEnd })));
            const laneBox = (key: string, inset: number) => {
              const l = lanes.get(key) ?? { col: 0, cols: 1 };
              const w = 100 / l.cols;
              return { left: `calc(${l.col * w}% + ${inset}px)`, width: `calc(${w}% - ${inset * 2}px)` };
            };
            return (
              <div key={dayIdx} style={{ position:"relative", borderLeft:`1px solid ${T.border}` }}>
                {HOURS.map((h) => <div key={h} style={{ height:ROW_H, borderBottom:`1px solid ${alpha(T.border,"80")}` }}/>)}
                {/* First read still pending: grey blocks shaped like events, so an empty grid does not read as "no events"
                    (CC, 2026-10-05). One announcement for the whole grid, on the first column. */}
                {calEvents === null && !loadError ? (() => {
                  const shapes = [{ top: 1, h: 2 }, { top: 4.5, h: 1.5 }]; // ui-config
                  const blocks = shapes.map((sh, i) => <div key={i} style={{ position:"absolute", left:4, right:4, top:sh.top*ROW_H + (dayIdx%2)*ROW_H, height:sh.h*ROW_H - 2 }}><SkeletonBox height="100%" /></div>);
                  return dayIdx === 0
                    ? <LoadingRegion label={t("eventday.eventDay.text21", "Loading events…")} testId="calendar-week-ghost" style={{ position:"absolute", inset:0 }}>{blocks}</LoadingRegion>
                    : <div aria-hidden="true" data-testid="calendar-week-ghost" style={{ position:"absolute", inset:0 }}>{blocks}</div>;
                })() : null}
                {daySpans.map(({ b, span }) => {
                  const top = (span.startH - HOURS[0]) * ROW_H;
                  const height = (span.endH - span.startH) * ROW_H;
                  const who = b.scope === "all-staff" ? t("calendar.week.allStaff", "All staff") : (b.staffName || t("calendar.week.staff", "Staff"));
                  return (
                    <div key={`${b.id}-${dayIdx}`} title={[b.title, who, b.blockType].filter(Boolean).join(" · ")}
                      style={{ position:"absolute", left:2, right:2, top, height:Math.max(height-2,18), borderRadius:4, background:`repeating-linear-gradient(-45deg, ${alpha(T.n3,"2E")} 0px, ${alpha(T.n3,"2E")} 5px, ${alpha(T.n3,"14")} 5px, ${alpha(T.n3,"14")} 10px)`, border:`1px dashed ${T.n4}`, padding:"3px 7px", overflow:"hidden", zIndex:1 }}>
                      <div style={{ fontSize:11, fontWeight:600, color:T.n2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.title}</div>
                      {height > 34 ? <div style={{ fontSize:10.5, color:T.soft, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{who}{b.blockType ? t("calendar.week.text3", "· {blockType}", { blockType: b.blockType }) : ""}</div> : null}
                    </div>
                  );
                })}
                {dayEvs.map((e) => {
                  const top = (e.gStart - HOURS[0]) * ROW_H;
                  const height = (e.gEnd - e.gStart) * ROW_H;
                  return (
                    <div key={e.id} onClick={() => { const full = uiEvents.find((u) => u.id === e.id); if (full) { nav.openEvent(full); return; } toast({ tone:"info", title:e.title, subtitle:i18nT("calendar.week.fmthmEStartFmthmEEndELoc.icu", "{value0} – {value1} · {value2}", { value0: calendarBlockTime(e.start), value1: calendarBlockTime(e.end), value2: e.loc??"" })}); }}
                      style={{ position:"absolute", ...laneBox(e.id, 4), top, height:Math.max(height-2,20), borderRadius:6, background:alpha(e.color,"22"), borderLeft:`3px solid ${e.color}`, padding:"3px 7px", cursor:"pointer", overflow:"hidden", zIndex:2 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:e.color, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{e.title}</div>
                      {height > 32 ? <div style={{ fontSize:11, color:T.soft }}>{calendarBlockTime(e.start)} – {calendarBlockTime(e.end)}{e.loc ? t("calendar.week.text4", "· {loc}", { loc: e.loc }) : ""}</div> : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </Screen>
  );
}
